import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { Input } from "components/ui/inputs/Input"
import { OptionCard } from "components/ui/inputs/OptionCard"
import { Slider } from "components/ui/inputs/Slider"
import { useErrors } from "providers/ErrorsProvider"
import { useState, useEffect, useMemo } from "react"
import styled, { useTheme } from "styled-components"
import { calculateAadt } from "./Aadt.calculate"
import { CalculationOutput } from "components/ui/display/CalculationOutput"
import { Bar, BarChart, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts"
import { generateTicks } from "utilities/generateTicks"

export const Aadt = () => {

    const GlobalTheme = useTheme();
    const weeks = [];
    for(let i = 0; i <= 52; i++)
    {
        weeks.push({
            value: i,
            name: "Uke " + (i+1)
        });
    }

    const {setError, unsetError} = useErrors();

    const [rawInput, setRawInput] = useState({
        aadt: '',
        hour: 12,
        weekday: 3,
        week: 24,
    });

    const [calculationInput, setCalculationInput] = useState({
        aadt: '',
        hour: 12,
        weekday: 3,
        week: 24,
        mCurve: 1
    });

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateAadt(calculationInput);
        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }
        
        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });
        
    }, [calculationInput, setError, unsetError]);

    const X1AxisTicks = useMemo(() => {
        return generateTicks({
            min: 0,
            max: 23,
            step: 1,
        });
    }, []);

    const X3AxisTicks = useMemo(() => {
        return generateTicks({
            min: 0,
            max: 52,
            step: 1,
        });
    }, []);

    const Y1AxisTicks = useMemo(() => {
        if(!calculatedState.output?.hourlyTrafficData?.valid || !calculatedState.output?.hourlyTrafficDataMax?.valid) return null;

        return generateTicks({
            min: 0,
            max: calculatedState.output.hourlyTrafficDataMax.value,
            targetTicks: 10,
            expand: true
        });
    }, [calculatedState]);

    const Y2AxisTicks = useMemo(() => {
        if(!calculatedState.output?.dailyTrafficData?.valid || !calculatedState.output?.dailyTrafficDataMax?.valid) return null;

        return generateTicks({
            min: 0,
            max: calculatedState.output.dailyTrafficDataMax.value,
            targetTicks: 10,
            expand: true
        });
    }, [calculatedState]);

    const Y3AxisTicks = useMemo(() => {
        if(!calculatedState.output?.weeklyTrafficData?.valid || !calculatedState.output?.weeklyTrafficData?.valid) return null;

        return generateTicks({
            min: 0,
            max: calculatedState.output.weeklyTrafficDataMax.value,
            targetTicks: 10,
            expand: true
        });
    }, [calculatedState]);

    const dailyTrafficDataLabels = ["Man","Tir","Ons","Tor","Fre","Lør","Søn"];

    return (
        <StyledAadt>
            <ToolsColumn className='inputColumn'>
                <ToolsContainer title='Inndata'>
                    <Input
                        title="Årsdøgntrafikk"
                        prefix={<span>ADT</span>}
                        sufix='kjt/d.'
                        type='number'
                        error={calculatedState.input?.aadt?.severity === 'error' ?? null}
                        warning={calculatedState.input?.aadt?.severity === 'warning' ?? null}
                        value={rawInput.aadt}
                        onChange={(e) => setRawInput(prev => ({...prev, aadt: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, aadt: e.target.value}))}
                    />
                    <Slider
                        title='Klokkeslett'
                        values={[
                            {value: 0, name: '00:00'},
                            {value: 1, name: '01:00'},
                            {value: 2, name: '02:00'},
                            {value: 3, name: '03:00'},
                            {value: 4, name: '04:00'},
                            {value: 5, name: '05:00'},
                            {value: 6, name: '06:00'},
                            {value: 7, name: '07:00'},
                            {value: 8, name: '08:00'},
                            {value: 9, name: '09:00'},
                            {value: 10, name: '10:00'},
                            {value: 11, name: '11:00'},
                            {value: 12, name: '12:00'},
                            {value: 13, name: '13:00'},
                            {value: 14, name: '14:00'},
                            {value: 15, name: '15:00'},
                            {value: 16, name: '16:00'},
                            {value: 17, name: '17:00'},
                            {value: 18, name: '18:00'},
                            {value: 19, name: '19:00'},
                            {value: 20, name: '20:00'},
                            {value: 21, name: '21:00'},
                            {value: 22, name: '22:00'},
                            {value: 23, name: '23:00'},
                        ]}
                        defaultValue={12}
                        onChange={() => {}}
                        onChangeEnd={(e) => setCalculationInput(prev => ({...prev, hour: e}))}
                    />
                    <Slider
                        title='Klokkeslett'
                        values={[
                            {value: 0, name: 'Mandag'},
                            {value: 1, name: 'Tirsdag'},
                            {value: 2, name: 'Onsdag'},
                            {value: 3, name: 'Torsdag'},
                            {value: 4, name: 'Fredag'},
                            {value: 5, name: 'Lørdag'},
                            {value: 6, name: 'Søndag '},
                        ]}
                        defaultValue={3}
                        onChange={() => {}}
                        onChangeEnd={(e) => setCalculationInput(prev => ({...prev, weekday: e}))}
                    />
                    <Slider
                        title='Uke'
                        values={weeks}
                        defaultValue={24}
                        onChange={() => {}}
                        onChangeEnd={(e) => setCalculationInput(prev => ({...prev, week: e}))}
                    />
                    
                </ToolsContainer>
                <ToolsContainer title='M-kurve'>
                    <OptionCard
                        icon={'M1'}
                        title="By-/boliggate"
                        description="Liten trafikk i sommerferien (75-85 % av ÅDT). Døgntrafikken lørdag og søndag er betydelig mindre enn på virkedager."
                        selected={calculationInput.mCurve === 1}
                        onChange={() => setCalculationInput(prev => ({...prev, mCurve: 1}))}
                    />
                    <OptionCard
                        icon={'M2'}
                        title="Hovedveg i bystrøk"
                        description="Mindre trafikk i januar og februar (90-95% av ÅDT). I sommerferien ligger trafikken mellom 95 100 % av ÅDT. Døgntrafikken lørdag og søndag er betydelig mindre enn på virkedager."
                        selected={calculationInput.mCurve === 2}
                        onChange={() => setCalculationInput(prev => ({...prev, mCurve: 2}))}
                    />
                    <OptionCard
                        icon={'M3'}
                        title="Hovedveg med innslag av sesongtrafikk"
                        description="Litt større trafikk i sommerferien enn ellers i året (110-115 % av ÅDT). Døgntrafikken lørdag og søndag er betydelig mindre enn på virkedager (80 % av Ukedøgntrafikk)."
                        selected={calculationInput.mCurve === 3}
                        onChange={() => setCalculationInput(prev => ({...prev, mCurve: 3}))}
                    />
                    <OptionCard
                        icon={'M4'}
                        title="Hovedveg i tettbygdstrøk med stor helgetrafikk"
                        description="Større trafikk i sommerferien enn ellers i året (i underkant av 130 % av ÅDT). Døgntrafikken på lørdag er lavere enn de øvrige dagene i uka."
                        selected={calculationInput.mCurve === 4}
                        onChange={() => setCalculationInput(prev => ({...prev, mCurve: 4}))}
                    />
                    <OptionCard
                        icon={'M5'}
                        title="Hovedveg utenfor tettbygd strøk"
                        description="Topptrafikk i sommerferien (ca 150 % av ÅDT). Døgntrafikken fredag er betydelig større enn de øvrige ukedagene. Døgntrafikken på søndag er litt større enn på øvrige ukedager."
                        selected={calculationInput.mCurve === 5}
                        onChange={() => setCalculationInput(prev => ({...prev, mCurve: 5}))}
                    />
                    <OptionCard
                        icon={'M6'}
                        title="Transportårer med stor sommertrafikk"
                        description="Topptrafikk i sommerferien (ca 200 % av ÅDT). Døgntrafikken fredag og søndag er litt større enn på de øvrige ukedagene."
                        selected={calculationInput.mCurve === 6}
                        onChange={() => setCalculationInput(prev => ({...prev, mCurve: 6}))}
                    />
                    <OptionCard
                        icon={'M7'}
                        title="Turistrute med høy sommerdøgntrafikk"
                        description="Topptrafikk i sommerferie, vinterferie og påskeferie. Døgntrafikken i toppsesongen kan være opptil ca 3 x ÅDT. Døgntrafikken fredag og søndag er betydelig større enn på virke- dager."
                        selected={calculationInput.mCurve === 7}
                        onChange={() => setCalculationInput(prev => ({...prev, mCurve: 7}))}
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className='outputColumn'>
                <CalculationOutput
                    prefix={<span>Q<sub>est.</sub></span>}
                    sufix={<span>kjt/t</span>}
                    title='Estimert timetrafikk'
                    value={calculatedState.output?.hourlyTraffic?.value ?? '--'}
                />
                <CalculationOutput
                    prefix={<span>Q<sub>maks</sub></span>}
                    sufix={<span>kjt/t</span>}
                    title='Døgnets høyeste timetrafikk'
                    value={calculatedState.output?.dailyTopTraffic?.value ?? '--'}
                />
                <CalculationOutput
                    sufix={<span>kjt/t</span>}
                    title='Døgnets kurvevariasjon'
                    value={calculatedState.output?.curveVariation?.value ?? '--'}
                />
            </ToolsColumn>
            <ToolsColumn className='infoColumn'>
                {
                    calculatedState.output?.hourlyTrafficData?.valid ? 
                    <ToolsContainer title="Timetrafikk per klokkeslett">
                        <div className="graphContainer">
                            <ResponsiveContainer
                                height='100%'
                                width='100%'
                                minWidth='400px'
                                minHeight='0'
                                aspect={undefined}
                            >
                                <LineChart
                                    data={calculatedState.output.hourlyTrafficData.value}
                                    dataKey={'hour'}
                                    margin={{top: 10, left: 30, right: 30, bottom: 0}}
                                >
                                    {
                                        calculatedState.output.hourlyTrafficData.value.map((_, i) =>
                                            <Line
                                                type={'monotone'}
                                                key={i}
                                                dataKey={'m' + (i + 1)}
                                                dot={false}
                                                strokeWidth={1}
                                                stroke={GlobalTheme.colors.mutedForeground}
                                                hide={(i + 1) === calculatedState.input.mCurve.value}
                                            />
                                        )
                                    }
                                    <Line
                                        dataKey={'m' + calculatedState.input.mCurve.value}
                                        dot={false}
                                        strokeWidth={4}
                                        stroke={GlobalTheme.colors.accentForeground}
                                        type={'monotone'}
                                    />
                                    <XAxis
                                        dataKey={'hour'}
                                        type='number'
                                        height={50}
                                        fontSize={12}
                                        ticks={X1AxisTicks}
                                        domain={['dataMin', 'dataMax']}
                                        label={{
                                            value: 'Klokkeslett [time]',
                                            position: 'center',
                                            dy: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                    <YAxis
                                        orientation={'left'}
                                        fontSize={12}
                                        ticks={Y1AxisTicks}
                                        label={{
                                            value: 'Trafikk [kjt/t]',
                                            angle: -90,
                                            position: 'left',
                                            dx: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ToolsContainer>
                    : null
                }
                {
                    calculatedState.output?.dailyTrafficData?.valid ? 
                    <ToolsContainer title="Døgntrafikk per ukedag">
                        <div className="graphContainer">
                            <ResponsiveContainer
                                height='100%'
                                width='100%'
                                minWidth='400px'
                                minHeight='0'
                                aspect={undefined}
                            >
                                <BarChart
                                    data={calculatedState.output.dailyTrafficData.value}
                                    //dataKey={'hour'}
                                    margin={{top: 10, left: 30, right: 30, bottom: 0}}
                                >
                                    {
                                        calculatedState.output.dailyTrafficData.value.map((el, i) =>
                                            <Bar
                                                key={i}
                                                dataKey={'m' + (i + 1)}
                                                shape={(props) => <CustomBar {...props} isActiveWeekday={props.index === calculatedState.input.weekday.value} isActiveCurve={i+1 === calculatedState.input.mCurve.value}/>}
                                            />
                                        )
                                    }
                                    <YAxis
                                        orientation={'left'}
                                        fontSize={12}
                                        ticks={Y2AxisTicks}
                                        label={{
                                            value: 'Trafikk [kjt/d]',
                                            angle: -90,
                                            position: 'left',
                                            dx: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                    <XAxis
                                        tickFormatter={(v) => dailyTrafficDataLabels[v]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </ToolsContainer>
                    : null
                }
                {
                    calculatedState.output?.weeklyTrafficData?.valid ? 
                    <ToolsContainer title='Uketrafikk per uke'>
                        <div className="graphContainer">
                            <ResponsiveContainer
                                height='100%'
                                width='100%'
                                minWidth='400px'
                                minHeight='0'
                                aspect={undefined}
                            >
                                <LineChart
                                    data={calculatedState.output.weeklyTrafficData.value}
                                    dataKey={'hour'}
                                    margin={{top: 10, left: 30, right: 30, bottom: 0}}
                                >
                                    {
                                        calculatedState.output.weeklyTrafficData.value.map((_, i) =>
                                            <Line
                                                type={'monotone'}
                                                key={i}
                                                dataKey={'m' + (i + 1)}
                                                dot={false}
                                                strokeWidth={1}
                                                stroke={GlobalTheme.colors.mutedForeground}
                                                hide={(i + 1) === calculatedState.input.mCurve.value}
                                            />
                                        )
                                    }

                                    <Line
                                        dataKey={'m' + calculatedState.input.mCurve.value}
                                        dot={false}
                                        strokeWidth={4}
                                        stroke={GlobalTheme.colors.accentForeground}
                                        type={'monotone'}
                                    />
                                    <XAxis
                                        dataKey={'week'}
                                        type='number'
                                        height={50}
                                        fontSize={12}
                                        ticks={X3AxisTicks}
                                        domain={['dataMin', 'dataMax']}
                                        label={{
                                            value: 'Uke',
                                            position: 'center',
                                            dy: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                    <YAxis
                                        orientation={'left'}
                                        fontSize={12}
                                        ticks={Y3AxisTicks}
                                        label={{
                                            value: 'Trafikk [kjt/t]',
                                            angle: -90,
                                            position: 'left',
                                            dx: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ToolsContainer>
                    : null
                }
            </ToolsColumn>
        </StyledAadt>
    )
}

const StyledAadt = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }

    div.graphContainer
    {
        min-height: 300px;
        overflow: auto;

    }
`

const CustomBar = (props) => {
    const GlobalTheme = useTheme();
    const { x, y, width, height, isActiveWeekday, isActiveCurve} = props;

    return (
        <g>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={isActiveCurve ? GlobalTheme.colors.accentForeground : isActiveWeekday ? GlobalTheme.colors.accentBackground: GlobalTheme.colors.mutedForeground}
                //strokeWidth={1}
                rx={2}
                ry={2}
            />
        </g>
    );
}